import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Shield, CheckCircle2, AlertCircle } from "lucide-react";

export default function PrivacyRequestPage() {
  const [form, setForm] = useState({ email: "", request_type: "access", details: "" });
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setOk(""); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/privacy/request", form);
      setOk(data.message);
      setForm({ email: "", request_type: "access", details: "" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-request-page">
      <header className="sticky top-0 bg-white border-b-2 border-asap-deep z-30">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/"><Logo size={56} compact/></Link>
          <Link to="/legal" className="px-4 py-2 border-2 border-asap-deep font-bold uppercase text-sm hover:bg-asap-deep hover:text-white">Legal Library →</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">CCPA · CPRA · GDPR</div>
        <h1 className="font-display text-4xl uppercase text-asap-deep mt-2 flex items-center gap-3"><Shield size={36}/> Your Privacy Rights</h1>
        <p className="text-slate-600 mt-2">Residents of California, Colorado, Virginia, Connecticut, Utah, and the EU/UK have rights to access, delete, correct, or opt out of the sharing of their personal data. Submit a request below — we respond within 45 days.</p>

        <form onSubmit={submit} className="mt-8 bg-white border-2 border-asap-deep p-6 shadow-hard space-y-4" data-testid="privacy-form">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Email on file</div>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2 border-2 border-asap-deep outline-none" data-testid="privacy-email"/>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Request type</div>
            <select value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })} className="w-full px-4 py-2 border-2 border-asap-deep outline-none bg-white" data-testid="privacy-type">
              <option value="access">Access — see what data you have on me</option>
              <option value="delete">Delete — erase my personal data</option>
              <option value="correct">Correct — fix inaccurate data</option>
              <option value="opt_out">Opt-out — of sharing / targeted advertising</option>
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">Details (optional)</div>
            <textarea rows={4} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="w-full px-4 py-2 border-2 border-asap-deep outline-none" placeholder="Anything else we should know?" data-testid="privacy-details"/>
          </label>
          {ok && <div className="p-3 bg-emerald-50 border-2 border-emerald-500 text-emerald-700 font-bold text-sm inline-flex items-center gap-2"><CheckCircle2 size={16}/> {ok}</div>}
          {err && <div className="p-3 bg-red-50 border-2 border-asap-red text-asap-red font-bold text-sm inline-flex items-center gap-2"><AlertCircle size={16}/> {err}</div>}
          <button type="submit" disabled={busy} data-testid="privacy-submit" className="w-full px-5 py-3 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-deep shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Request"}
          </button>
          <p className="text-xs text-slate-500">We verify identity before fulfilling deletion or correction requests. You'll receive a confirmation email once processing begins.</p>
        </form>
      </main>
    </div>
  );
}
